import React, { type FC, useState } from 'react'

import { AlertDialog, Button, Icon, Tooltip, Input } from '@awell-health/design-system'
import { formatDate } from '@awell/ui-kit/utils'
import { isNil } from 'lodash'
import { useFeatureGate } from '@statsig/react-bindings'
import { FeatureGateName } from '@awell/libs-web/experiments'

import type { ActivityContent, Activity } from '../../../types'
import { createActionIcon } from '../../helperFunctions/getActivityIcon'
import { createActivityTitle } from '../../helperFunctions/getActivityTitle'
import { useExpireTimer } from 'app-care/src/hooks/useExpireTimer'
import { AgentCardDesription } from './AgentCardDetails'
import { isHITL } from './activityCardHelpers'
import { useExpireActivity } from '../../../../../hooks/useExpireActivity'

interface Props {
  onSelect: () => void
  activity: Activity
  content: ActivityContent
  completed: boolean
  isAgentActivity: boolean
  isAgentActivityInitiator: boolean
}

export const ActivityBody: FC<Props> = ({
  onSelect = () => null,
  activity,
  content,
  completed,
  isAgentActivity,
  isAgentActivityInitiator
}) => {
  const [openTimerModal, setOpenTimerModal] = useState(false)
  const [openActivityModal, setOpenActivityModal] = useState(false)
  const [expireReason, setExpireReason] = useState<string>('')
  const { value: isActivityExpirationEnabled } = useFeatureGate(FeatureGateName.EXPIRE_ACTIVITY)
  const isTimerActivity = activity.object.type === 'TIMER'
  const canExpireActivity =
    !isTimerActivity && activity.reference_type !== 'AGENT' && !completed && isActivityExpirationEnabled
  // fetch last sub activity if there is no schedued date it means there was an error so we don't want
  // to show this anyways
  const scheduledDate = activity.sub_activities[activity.sub_activities.length - 1]?.scheduled_date ?? null
  const { expireTimer } = useExpireTimer()
  const { expireActivity } = useExpireActivity()

  const completeTimer = async (): Promise<void> => {
    setOpenTimerModal(false)
    await expireTimer(activity.id)
  }

  const onExpireActivity = async (): Promise<void> => {
    setOpenActivityModal(false)
    setExpireReason('')
    await expireActivity(activity.id, expireReason)
  }

  const handleCloseActivityModal = (): void => {
    setOpenActivityModal(false)
    setExpireReason('')
  }

  const activityTitle = createActivityTitle(activity)
  const stepName = isAgentActivity ? content.stepName.split('-')[0] : content.stepName

  return (
    <div>
      <div className="flex flex-col gap-4">
        <div className="flex gap-4">
          <div>{createActionIcon(activity)}</div>
          <div className="flex flex-col overflow-hidden">
            <div className="justify-start text-slate-600 text-lg leading-5 overflow-hidden text-ellipsis whitespace-nowrap">
              {activityTitle}
            </div>
            <div className="justify-start text-slate-600 text-sm font-normal overflow-hidden text-ellipsis whitespace-nowrap">
              {content.trackName ?? ''} | {stepName ?? `${activity.label?.text}`}{' '}
              {isAgentActivity && isAgentActivityInitiator && activity.object.name}
            </div>
            {!isNil(scheduledDate) && isTimerActivity && (
              <span className="text-neutral-500 text-base mt-2">
                {`Wait until ${formatDate({
                  isoDateStr: scheduledDate,
                  dateFormat: 'dd MMM yyyy, h:mm:ss a' // 10 Jan 2025, 5:53:00 PM
                })}`}
              </span>
            )}
          </div>
          <div className="flex flex-shrink-0 gap-2 ml-auto">
            {isTimerActivity && !completed && (
              <Tooltip placement="top" datatip="Skip the timer">
                <Button
                  variant="secondaryBlue"
                  shape="squareSm"
                  size={null}
                  disabled={completed}
                  onClick={() => setOpenTimerModal(true)}
                >
                  <Icon icon="RiPlayFill" />
                </Button>
              </Tooltip>
            )}
            {canExpireActivity && (
              <Tooltip placement="top" datatip="Cancel activity">
                <Button onClick={() => setOpenActivityModal(true)} variant="secondaryBlue" shape="squareSm" size={null}>
                  <Icon icon="RiPlayFill" />
                </Button>
              </Tooltip>
            )}
            <Button onClick={onSelect} variant={completed ? 'secondaryBlue' : 'primary'}>
              {completed ? content.buttonTextComplete : content.buttonTextPending}
            </Button>
          </div>
        </div>
        {(isAgentActivity || isHITL(activity)) && <AgentCardDesription activity={activity} />}
        {openTimerModal && (
          <AlertDialog
            title={'Are you sure you want to skip the timer?'}
            text={'The care flow will continue immediately. This action cannot be undone.'}
            button={{
              label: 'Skip timer',
              buttonAction: () => {
                void completeTimer()
              }
            }}
            secondaryButton={{
              label: 'Cancel',
              buttonAction: () => setOpenTimerModal(false)
            }}
          />
        )}
        {openActivityModal && (
          <AlertDialog
            title={'Are you sure you want to cancel the activity?'}
            text={
              'Cancelling will expire the activity. This action cannot be undone. If the activity is collecting data that is later needed in the care flow, it will be missing.'
            }
            button={{
              label: 'Expire activity',
              buttonAction: () => {
                void onExpireActivity()
              }
            }}
            secondaryButton={{
              label: 'Cancel',
              buttonAction: handleCloseActivityModal
            }}
          >
            <div className="mt-6">
              <Input
                label="Reason for expiring activity (optional)"
                placeholder="Enter a reason for expiring this activity..."
                value={expireReason}
                onChange={e => setExpireReason(e.target.value)}
              />
            </div>
          </AlertDialog>
        )}
      </div>
    </div>
  )
}

ActivityBody.displayName = 'ActivityBody'
